import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { isReviewItemResolved } from "@utils/reportVisibility";
import { useFormContext } from "react-hook-form";

import {
  GetReportReviewItemsQuery,
  ReviewItemFieldsFragment,
  useApplyReportCommentMutation,
  useUpdateReportSectionFieldMutation,
} from "@api/graphql/types-and-hooks";
import { toAppliedValue } from "@components/QaReview/PeerReview/peerReview.readableValue";
import { isConflicting } from "@components/QaReview/PeerReview/peerReview.suggestedEdit";
import { invalidateReviewItems, settleReviewMutation } from "@components/QaReview/hooks/reviewMutations";
import { useAdvancePast } from "@components/QaReview/hooks/useResolveItem";
import { useReviewFields } from "@components/QaReview/hooks/useReviewFields";
import { useReviewSession } from "@components/QaReview/hooks/useReviewSession";
import { notifyError } from "@components/notifications/notifications";
import { useReport } from "@providers/ReportProvider/useReport";

// Reads the comment from a fresh fetch, so an edit deleted or resolved by someone else since the last poll
// is not written into the report. Only the panel's own (active) query counts: an older source variant left
// in the cache can still hold the comment from before it was deleted. A failed fetch throws, so stale data
// is never taken as current.
const LIVE_REVIEW_ITEMS = { queryKey: ["getReportReviewItems"], type: "active" } as const;

const fetchLatestComment = async (queryClient: QueryClient, id: string) => {
  await queryClient.refetchQueries(LIVE_REVIEW_ITEMS, { throwOnError: true });
  return queryClient
    .getQueriesData<GetReportReviewItemsQuery>(LIVE_REVIEW_ITEMS)
    .flatMap(([, data]) => data?.getReportReviewItems ?? [])
    .find((item) => item?.id === id);
};

export const useApplyComment = (options: { advance?: boolean } = {}) => {
  const queryClient = useQueryClient();
  const { state, dispatch } = useReviewSession();
  const fieldsById = useReviewFields();
  const advancePast = useAdvancePast(options);
  const { resetField } = useFormContext();
  const {
    reportQuery: { refetch: refetchReport },
  } = useReport();
  const { mutateAsync: updateField } = useUpdateReportSectionFieldMutation();
  const { mutateAsync: applyComment } = useApplyReportCommentMutation({
    onSuccess: async (data) => {
      const toasts = { failure: "Failed to apply suggested edit", success: "Suggested edit applied" };
      if (settleReviewMutation(data?.applyReportComment, toasts)) await invalidateReviewItems(queryClient);
    },
    onError: () => notifyError("Error applying suggested edit. Please try again."),
  });

  // The backend's applyReportComment only flips status, so the proposed value is written to the field first.
  const apply = async (comment: ReviewItemFieldsFragment) => {
    if (comment.__typename !== "ReportCommentType" || comment.proposedValue == null || state.applyingFieldId) return;
    const fieldId = comment.reportSectionField?.id;
    const field = fieldId ? fieldsById[fieldId] : undefined;
    if (!field) {
      notifyError("Could not find the field for this suggested edit.");
      return;
    }
    // Accepting closes the comment, so it moves on like Resolve does (the card's checkmark does either).
    const step = advancePast(comment);
    const content = toAppliedValue(field.realField.fieldType, field.content, comment.proposedValue);
    dispatch({ type: "setApplyingField", fieldId: field.id });
    try {
      let latest: Awaited<ReturnType<typeof fetchLatestComment>>;
      try {
        latest = await fetchLatestComment(queryClient, comment.id);
      } catch {
        notifyError("Could not check this suggested edit is still open. Please try again.");
        return;
      }
      if (!latest || (latest.__typename === "ReportCommentType" && latest.isDeleted)) {
        notifyError("This comment was deleted.");
        return;
      }
      if (isReviewItemResolved(latest)) {
        notifyError("This suggested edit was already resolved.");
        return;
      }
      try {
        const result = await updateField({
          reportSectionId: field.sectionId,
          reportSectionFieldId: field.id,
          content,
        });
        if (!result.updateReportSectionField?.status) {
          notifyError(result.updateReportSectionField?.message || "Failed to apply suggested edit");
          return;
        }
      } catch {
        notifyError("Error applying suggested edit. Please try again.");
        return;
      }
      resetField(field.id, { defaultValue: content });
      dispatch({ type: "remountField", fieldId: field.id });
      await refetchReport();
      const result = await applyComment({ id: comment.id }).catch(() => undefined);
      if (result?.applyReportComment?.success) step();
    } finally {
      dispatch({ type: "setApplyingField", fieldId: null });
    }
  };

  const requestApply = (comment: ReviewItemFieldsFragment) => {
    if (isConflicting(comment, fieldsById)) {
      dispatch({ type: "setConflictComment", commentId: comment.id, advance: options.advance });
      return;
    }
    void apply(comment);
  };

  return { apply, requestApply };
};
